import React from 'react';
import { EyeIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, CalendarIcon, UserIcon, BarChartIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
export function VendorCard({
  vendor,
  onViewVendor,
  getAvailabilityIcon,
  getAvailabilityText,
  locale,
  isESGEnabled = false
}) {
  // Format date based on locale
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  // Get ESG status label and color
  const getESGStatusInfo = score => {
    if (score >= 80) return {
      label: 'Excellent',
      color: 'green'
    };
    if (score >= 60) return {
      label: 'Good',
      color: 'blue'
    };
    if (score >= 40) return {
      label: 'Moderate',
      color: 'yellow'
    };
    return {
      label: 'Needs Review',
      color: 'red'
    };
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200" data-id="element-20270">
      {/* Card Header */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-20271">
        <div className="flex justify-between items-start" data-id="element-20272">
          <div data-id="element-20273">
            <h3 className="text-lg leading-6 font-medium text-gray-900 truncate" title={vendor.name} data-id="element-20274">
              {vendor.name}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 truncate" data-id="element-20275">
              {vendor.id}
            </p>
          </div>
          <div data-id="element-20276">
            <StatusBadge status={vendor.status.toLowerCase()} text={vendor.status} data-id="element-20277" />
          </div>
        </div>
      </div>
      {/* Card Body */}
      <div className="px-4 py-5 sm:p-6" data-id="element-20278">
        <div className="grid grid-cols-2 gap-4" data-id="element-20279">
          <div data-id="element-20280">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20281">
              Type
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-20282">
              {vendor.type || 'Not specified'}
            </p>
          </div>
          <div data-id="element-20283">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20284">
              Country
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-20285">{vendor.country}</p>
          </div>
          <div data-id="element-20286">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20287">
              Onboarded
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-20288">
              {formatDate(vendor.onboardingDate)}
            </p>
          </div>
          <div data-id="element-20289">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20290">
              Last Updated
            </h4>
            <p className="mt-1 text-sm text-gray-900" data-id="element-20291">
              {formatDate(vendor.lastUpdated)}
            </p>
          </div>
        </div>
        <div className="mt-4" data-id="element-20292">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20293">
            Availability
          </h4>
          <div className="mt-1 flex items-center text-sm text-gray-700" data-id="element-20294">
            {getAvailabilityIcon(vendor)}
            <span className="ml-1" data-id="element-20295">{getAvailabilityText(vendor)}</span>
          </div>
        </div>
        <div className="mt-4" data-id="element-20296">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20297">
            Compliance Status
          </h4>
          <div className="mt-1 flex items-center text-sm" data-id="element-20298">
            {vendor.complianceStatus === 'Compliant' && <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-green-500" data-id="element-20299" />}
            {vendor.complianceStatus === 'Pending Review' && <AlertTriangleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-yellow-500" data-id="element-20300" />}
            {(vendor.complianceStatus === 'Incomplete' || vendor.complianceStatus === 'Not Started') && <AlertTriangleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-20301" />}
            {vendor.complianceStatus === 'Non-Compliant' && <XCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-red-500" data-id="element-20302" />}
            <span className="text-gray-700" data-id="element-20303">{vendor.complianceStatus}</span>
          </div>
        </div>
        {/* ESG Score - Only show if enabled */}
        {isESGEnabled && <div className="mt-4" data-id="element-20304">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20305">
              ESG Score
            </h4>
            {vendor.esg && vendor.esg.score !== undefined ? <div className="mt-1" data-id="element-20306">
                <div className="flex items-center" data-id="element-20307">
                  <BarChartIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-500" data-id="element-20308" />
                  <span className="text-gray-900 font-medium" data-id="element-20309">
                    {vendor.esg.score}
                  </span>
                  <span className="text-xs text-gray-500 ml-1" data-id="element-20310">/100</span>
                  {(() => {
              const {
                label,
                color
              } = getESGStatusInfo(vendor.esg.score);
              return <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`} data-id="element-20311">
                        {label}
                      </span>;
            })()}
                </div>
              </div> : <div className="mt-1 text-sm text-gray-500" data-id="element-20312">Not Provided</div>}
          </div>}
        {vendor.contacts && vendor.contacts.length > 0 && <div className="mt-4" data-id="element-20313">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20314">
              Primary Contact
            </h4>
            <div className="mt-1 flex items-start" data-id="element-20315">
              <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400 mt-0.5" data-id="element-20316" />
              <div data-id="element-20317">
                <p className="text-sm text-gray-900" data-id="element-20318">
                  {vendor.contacts[0].name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-20319">
                  {vendor.contacts[0].email}
                </p>
              </div>
            </div>
          </div>}
      </div>
      {/* Card Footer */}
      <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200" data-id="element-20320">
        <div className="flex justify-end" data-id="element-20321">
          <button onClick={() => onViewVendor(vendor)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-5 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20322">
            <EyeIcon className="h-4 w-4 mr-1" data-id="element-20323" />
            View Details
          </button>
        </div>
      </div>
    </div>;
}